<template>
  <div
    class="py-10 px-4 lg:px-0"
    :class="{
      'md:pt-12 md:pb-[4.5rem] lg:py-20': layout === 'alternate-items',
      'lg:pt-[6.8125rem] lg:pb-[3.1875rem]': layout === 'default',
      'md:py-16': layout === 'two-columns'
    }"
  >
    <div
      class="w-full mx-auto"
      :class="{
        'max-w-wrapper-md': layout === 'alternate-items' || layout === 'two-columns',
        'max-w-wrapper-lg': layout === 'default'
      }"
    >
      <div
        :class="{
          'text-center': theme !== 'card',
          'lg:text-center': theme === 'card'
        }"
      >
        <component
          :is="headerTag"
          v-if="$slots.header"
          class="md:text-4.5xl text-2.5xl md:leading-[3rem] leading-9"
          :class="[
            {
              'text-left': layout === 'alternate-items',
              'text-left md:text-center md:!text-3.5xl md:!leading-10 md:!mb-10': layout === 'two-columns',
              'mb-6 md:mb-[4.3125rem]': !$slots['sub-header']
            },
            headerTitleClass
          ]"
        >
          <slot name="header"></slot>
        </component>

        <p
          v-if="$slots['sub-header']"
          :class="[
            {
              'text-left': layout === 'alternate-items'
            },
            subHeaderClass
          ]"
          class="text-lg pt-8 pb-12 empty:hidden"
        >
          <slot name="sub-header"></slot>
        </p>
      </div>

      <div
        class="flex flex-col md:flex-row gap-10 lg:gap-[2.125rem]"
        :class="[
          {
            'md:px-4 lg:px-0': layout === 'default',
            'justify-center items center': theme === 'card'
          },
          contentWrapperClass
        ]"
      >
        <div
          v-if="featuredImageUrl || featuredImageUrlMobile"
          :class="[
            featuredImageWrapperClass,
            {
              'flex justify-center items center': theme === 'card'
            }
          ]"
        >
          <img
            :class="[
              theme === 'card' ? 'my-auto md:ml-auto mr-auto' : 'm-auto',
              featuredImageUrlMobile ? 'hidden md:block' : ''
            ]"
            :src="featuredImageUrl"
            :width="featuredImageDimensions && featuredImageDimensions.width"
            :height="featuredImageDimensions && featuredImageDimensions.height"
            :loading="imageLazyLoad ? 'lazy' : 'eager'"
            alt="Saving Plan"
            class="md:min-w-[15.625rem]"
          />

          <img
            v-if="featuredImageUrlMobile"
            :class="
              theme === 'card'
                ? 'my-auto md:ml-auto mr-auto'
                : 'm-auto'
            "
            :src="featuredImageUrlMobile"
            :width="featuredImageDimensions && featuredImageDimensions.width"
            :height="featuredImageDimensions && featuredImageDimensions.height"
            :loading="imageLazyLoad ? 'lazy' : 'eager'"
            alt="Saving Plan"
            class="block md:hidden md:min-w-[15.625rem]"
          />
        </div>

        <div
          class="flex flex-col lg:flex-1"
          :class="[
            {
              'gap-8': theme !== 'card',
              'gap-6': theme === 'card',
              'md:max-w-[25rem] lg:max-w-[28.125rem]': layout === 'default',
              'px-4 md:px-0': layout === 'default' && theme !== 'card',
              'md:!flex-row flex-wrap': layout === 'two-columns'
            },
            cardContainerClass
          ]"
        >
          <atoms-card
            v-for="(item, itemKey) in verticals"
            :key="itemKey"
            :size="theme === 'card' ? 'md' : 'none'"
            :shadow="theme === 'card' ? 'lg' : 'none'"
            :theme="theme === 'card' ? 'default' : 'plain'"
            :rounded="theme === 'card' ? 'standard' : 'none'"
            :class="{
              'grid md:grid-cols-2 gap-2 md:gap-[2.0625rem]': layout === 'alternate-items',
              'flex-[1_1_33.33%]': layout === 'two-columns'
            }"
            class="text-left"
          >
            <div
              v-if="layout === 'alternate-items' && item.sectionImage"
              class="h-[11.25rem] md:h-[13.125rem] lg:h-[15rem] py-5 md:py-0 flex items-center md:justify-center"
              :class="{
                'md:order-2': layout === 'alternate-items' && (itemKey % 2)
              }"
            >
              <img
                :src="item.sectionImage"
                :loading="imageLazyLoad ? 'lazy' : 'eager'"
                :width="item.sectionImageDimensions && item.sectionImageDimensions.width"
                :height="item.sectionImageDimensions && item.sectionImageDimensions.height"
                :class="cardImageClass"
                class="max-h-full md:max-h-none"
                alt="Saving Plan"
              />
            </div>

            <div
              class="flex flex-col items-start gap-2"
              :class="{
                'md:order-1': layout === 'alternate-items' && (itemKey % 2),
                'flex flex-col justify-center md:gap-4': layout === 'alternate-items',
                '!gap-4': layout === 'two-columns'
              }"
            >
              <atoms-button
                v-if="theme !== 'card' && item?.productPageUrl"
                :id="strToSnakeCase(item?.title)"
                :to="item?.productPageUrl || ''"
                class="!flex !text-2xl !leading-8 !p-0"
                size="large"
                theme="tertiary"
                has-icon
              >
                {{ item.title }}
              </atoms-button>

              <div
                v-else-if="item.title"
                :class="{
                  'flex flex-row gap-6': layout === 'two-columns'
                }"
                class="w-full lg:w-auto"
              >
                <nuxt-img
                  v-if="layout === 'two-columns'"
                  :src="item.sectionImage"
                  :loading="imageLazyLoad ? 'lazy' : 'eager'"
                  :width="item.sectionImageDimensions && item.sectionImageDimensions.width"
                  :height="item.sectionImageDimensions && item.sectionImageDimensions.height"
                  :class="cardImageClass"
                  class="w-[5rem] h-[5rem] object-cover"
                  alt="Saving Plan"
                />
                <p
                  class="text-2xl md:text-2.5xl md:leading-9 font-bold flex justify-center items-center gap-2"
                  :class="cardTitleClass"
                >
                  {{ item.title }}

                  <IconsChevron
                    v-if="cardTitleHasIcon"
                    ref="arrow"
                    class="fill-dark"
                    direction="right"
                    type="default"
                    size="default"
                  />
                </p>
              </div>

              <!-- eslint-disable vue/no-v-html -->
              <div
                class="text-base md:text-lg"
                :class="[
                  {
                    'md:leading-[1.5rem]': theme !== 'card',
                    'md:leading-[1.625rem]': theme === 'card'
                  },
                  cardDescriptionClass
                ]"
                v-html="item.description"
              >
              </div>
            </div>
          </atoms-card>
        </div>
      </div>

      <div
        v-if="$slots.footer"
        :class="footerClass"
        class="mt-10"
      >
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { arrayObjectPropValidator } from '~/helpers/validator'

defineOptions({
  name: 'HomeSavingPlan'
})

defineProps({
  layout: {
    type: String,
    default: 'default',
    validator: value => ['default', 'alternate-items', 'two-columns'].includes(value)
  },

  theme: {
    type: String,
    default: 'default',
    validator: value => ['default', 'card'].includes(value)
  },

  featuredImageUrl: {
    type: String,
    default: null
  },

  featuredImageUrlMobile: {
    type: String,
    default: null
  },

  featuredImageDimensions: {
    type: Object,
    default: () => ({})
  },

  featuredImageWrapperClass: {
    type: String,
    default: null
  },

  verticals: {
    type: Array,
    required: true,
    validator: value => arrayObjectPropValidator(value, ['title', 'description'])
  },

  contentWrapperClass: {
    type: String,
    default: ''
  },

  cardContainerClass: {
    type: String,
    default: ''
  },

  cardTitleClass: {
    type: String,
    default: ''
  },

  cardDescriptionClass: {
    type: String,
    default: ''
  },

  cardImageClass: {
    type: String,
    default: ''
  },

  footerClass: {
    type: String,
    default: ''
  },

  imageLazyLoad: {
    type: Boolean,
    default: false
  },

  cardTitleHasIcon: {
    type: Boolean,
    default: false
  },

  headerTitleClass: {
    type: String,
    default: ''
  },

  headerTag: {
    type: String,
    default: 'p',
    validator: value => ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div'].includes(value)
  },

  subHeaderClass: {
    type: String,
    default: ''
  }
})
</script>
